<template>
  <div class="min-h-screen bg-gradient-to-r from-purple-50 via-white to-purple-50 font-primary">

    <!-- Navbar -->
    <nav class="bg-white shadow-sm">
      <div class="w-full px-4 sm:px-12 lg:px-16 xl:px-28 2xl:px-36">
        <div class="flex items-center justify-between h-20">
          <NuxtLink to="/" class="flex items-center">
            <img src="~/assets/icon/app.png" alt="Aphelia" class="h-12 w-auto object-contain" />
            <span class="ml-2 text-xl font-semibold text-gray-900">Aphelia</span>
          </NuxtLink>
          <NuxtLink to="/"
            class="px-5 py-2 text-sm font-medium text-brand-color border border-brand-color rounded-lg transition-all cursor-pointer hover:bg-brand-color-100">
            Sign In
          </NuxtLink>
        </div>
      </div>
    </nav>

    <!-- Header / Hero -->
    <header class="w-full pt-12">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="inline-flex items-center gap-2 px-4 py-1.5 bg-white/60 backdrop-blur-sm rounded-full mb-4 shadow-sm">
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-color opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-brand-color"></span>
          </span>
          <span class="text-sm font-medium text-gray-600">We're here to help</span>
        </div>
        <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-brand-color mb-3">
          How can we help you today?
        </h1>
        <p class="text-gray-500 text-base md:text-lg max-w-xl mx-auto mb-8">
          Find answers, guides, and tutorials to help you get the most out of Aphelia.
        </p>

        <!-- Search -->
        <div class="relative max-w-2xl mx-auto group">
          <div class="absolute inset-0 bg-gradient-to-r from-brand-color/20 via-purple-400/20 to-pink-400/20 rounded-2xl blur-xl"></div>
          <div class="relative bg-white rounded-2xl shadow-lg border-2 border-transparent group-focus-within:border-brand-color/50 transition-all duration-300 overflow-hidden">
            <div class="flex items-center">
              <div class="pl-5 mt-0.5">
                <svg class="h-5 w-5 text-brand-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input v-model="searchQuery" type="text" placeholder="Search for help articles..."
                class="w-full px-4 py-4 bg-transparent border-0 focus:outline-none text-gray-700 placeholder-gray-400" />
              <button class="px-5 py-2 mr-3 bg-brand-color text-white text-sm font-medium rounded-xl hover:bg-semi-brand-color hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="w-full px-4 sm:px-12 lg:px-16 xl:px-28 2xl:px-36 py-12">

      <!-- Detail View -->
      <div v-if="selectedArticle">
        <button @click="selectedArticle = null"
          class="inline-flex items-center text-sm text-brand-color hover:text-semi-brand-color font-medium mb-6 cursor-pointer">
          <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to all topics
        </button>

        <div class="rounded-2xl shadow-md overflow-hidden">
          <div class="h-1.5" style="background: linear-gradient(to right, #a78bfa, #c4b5fd, #f9a8d4);"></div>
          <div class="bg-white p-6 sm:p-8">

          <div class="flex items-start gap-4 mb-6">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style="background: #716ACA;">
              <Icon :name="selectedArticle.icon" class="text-white" size="24" />
            </div>
            <div>
              <h2 class="text-xl font-bold text-gray-900">{{ selectedArticle.title }}</h2>
              <p class="text-sm text-gray-500 mt-1">{{ selectedArticle.description }}</p>
            </div>
          </div>

          <div class="border-t border-gray-100 pt-6">
            <div v-for="(section, sIdx) in selectedArticle.sections" :key="sIdx" class="mb-8 last:mb-0">
              <p v-if="section.question" class="text-sm italic text-gray-500 mb-3">{{ section.question }}</p>
              <div v-if="section.note"
                class="text-sm bg-purple-50 text-purple-800 border border-purple-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
                <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <span>{{ section.note }}</span>
              </div>
              <h3 v-if="section.heading" class="text-base font-semibold text-gray-800 mb-3">{{ section.heading }}</h3>
              <ol v-if="section.steps && section.steps.length" class="space-y-3">
                <li v-for="(step, i) in section.steps" :key="i" class="flex items-start gap-3">
                  <span
                    class="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-purple-500 text-white text-xs font-semibold flex items-center justify-center mt-0.5">
                    {{ i + 1 }}
                  </span>
                  <span class="text-sm text-gray-700 leading-relaxed">{{ step }}</span>
                </li>
              </ol>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Categories Grid -->
      <div v-else>
        <div v-for="category in filteredCategories" :key="category.name" class="mb-10">
          <h3 class="text-xl md:text-2xl font-bold text-brand-color mb-6">{{ category.name }}</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div v-for="article in category.articles" :key="article.title" @click="selectedArticle = article"
              class="rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden">
              <div class="h-1.5" style="background: linear-gradient(to right, #a78bfa, #c4b5fd, #f9a8d4);"></div>
              <div class="bg-white p-6">
              <div class="flex flex-col">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style="background: #716ACA;">
                  <Icon :name="article.icon" class="text-white" size="24" />
                </div>
                <h4 class="text-base font-semibold text-gray-800 group-hover:text-brand-color transition-colors mb-2">
                  {{ article.title }}
                </h4>
                <p class="text-gray-500 text-sm leading-relaxed line-clamp-2">{{ article.description }}</p>
                <div class="mt-4 flex items-center text-brand-color text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Learn more</span>
                  <svg class="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-if="filteredCategories.length === 0" class="text-center py-16">
          <svg class="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-sm text-gray-500">No articles found for "{{ searchQuery }}"</p>
          <button @click="searchQuery = ''" class="mt-2 text-sm text-brand-color hover:underline cursor-pointer">Clear search</button>
        </div>

        <!-- Still Need Help -->
        <div v-if="filteredCategories.length > 0" class="mt-12 text-center py-8">
          <h4 class="text-lg font-semibold text-brand-color mb-2">Still need help?</h4>
          <p class="text-gray-500 mb-4">Can't find what you're looking for? We're here to help!</p>
          <a href="mailto:support@aphelia.io"
            class="inline-block px-6 py-2.5 bg-gradient-to-r from-brand-color to-purple-500 text-white rounded-lg hover:shadow-lg hover:shadow-brand-color/30 transition-all duration-300 font-medium text-sm">
            Contact Us
          </a>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.1)]">
      <div class="flex flex-col gap-2 py-6 items-center justify-center">
        <p class="text-sm text-gray-500">
          &copy; {{ new Date().getFullYear() }} All rights reserved.
          <a href="https://technuf.com/" target="_blank" class="cursor-pointer hover:text-brand-color">Technuf LLC</a>
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'main',
})

useHead({
  title: 'Help Center - Aphelia Admin',
})

interface HelpSection {
  question?: string
  note?: string
  heading?: string
  steps?: string[]
}

interface HelpArticle {
  title: string
  description: string
  icon: string
  sections: HelpSection[]
}

interface HelpCategory {
  name: string
  articles: HelpArticle[]
}

const searchQuery = ref('')
const selectedArticle = ref<HelpArticle | null>(null)

const categories: HelpCategory[] = [
  {
    name: 'Getting Started',
    articles: [
      {
        title: 'About the Aphelia App',
        description: 'Learn what the Aphelia mobile app does.',
        icon: 'heroicons:information-circle',
        sections: [
          {
            question: 'What is the Aphelia App?',
            steps: [
              'The Aphelia App is a mobile application that allows users to log into their accounts.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Login & Security',
    articles: [
      {
        title: 'Enable Face ID / Touch ID',
        description: 'Set up biometric sign-in on the iOS app.',
        icon: 'heroicons:finger-print',
        sections: [
          {
            question: 'How do I Enable Sign in with Face ID/Touch ID?',
            note: 'Available only on the iOS mobile app.',
            steps: [
              'For the first time, Sign in with username and password.',
              'Click on the Enable button on the prompt to setup Face ID/Touch ID or press the toggle button on Settings present on the sidebar menu from dashboard.',
              'Use fingerprint for Touch ID or Face recognition for Face ID for authentication.',
            ],
          },
        ],
      },
      {
        title: 'Sign In with Face ID / Touch ID',
        description: 'Use your face or fingerprint to sign in.',
        icon: 'heroicons:user-plus',
        sections: [
          {
            question: 'How do I use Face ID/Touch ID to Sign In?',
            note: 'Available only on the iOS mobile app.',
            steps: [
              'Once Face ID/Touch ID is enabled, click on the face icon present on the right side of password field in the Sign in page.',
              'Use fingerprint for Touch ID or Face recognition for Face ID for authentication.',
            ],
          },
        ],
      },
      {
        title: 'Stay Logged In',
        description: 'Use Remember me on the iOS app.',
        icon: 'heroicons:lock-closed',
        sections: [
          {
            question: 'How do I stay logged in to my account?',
            note: 'Available only on the iOS mobile app.',
            steps: [
              'On the login page, enter email address.',
              'Enter password.',
              'Select Remember me.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Program Setup',
    articles: [
      {
        title: 'Create a Program',
        description: 'Add a program from the web or mobile app.',
        icon: 'heroicons:plus-circle',
        sections: [
          {
            question: 'How do I create a new program?',
            heading: 'Using the web application',
            steps: [
              'Open a web browser and sign in to the Aphelia application.',
              'From the dashboard, select Program from the left navigation menu.',
              'Select Add Program in the upper-right corner.',
              'Enter the required information and any other applicable details.',
              'If necessary, expand Advanced Settings and configure the applicable options.',
              'Select Save to create the program.',
            ],
          },
          {
            heading: 'Using the mobile application',
            steps: [
              'Open the Aphelia application and sign in.',
              'Select the Program card from the dashboard.',
              'Select the + icon in the upper-right corner.',
              'Enter the required information and any other applicable details.',
              'If necessary, expand Advanced Settings and configure the applicable options.',
              'Select Save to create the program.',
            ],
          },
        ],
      },
      {
        title: 'Manage a Program',
        description: 'Update, change the status, archive, or delete a program.',
        icon: 'heroicons:pencil-square',
        sections: [
          {
            question: 'How do I update/delete/archive a program?',
            steps: [
              'Navigate to Program and select the program you want to manage.',
              'To update the program, edit the applicable information and select Save.',
              'To change the program\'s status, open the status dropdown on the top right corner and select Draft, Active, or Closed.',
              'To archive the program, select Archived from the status dropdown, and then select Save.',
              'To delete the program, select Delete from the status dropdown and confirm the action if prompted.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'User Management',
    articles: [
      {
        title: 'Add a User',
        description: 'Create an Admin, Staff, Instructor, or Read Only user.',
        icon: 'heroicons:user-plus',
        sections: [
          {
            question: 'How do I add a new user (Admin/Staff/Instructor/Read Only)?',
            steps: [
              'From the dashboard, select the Users card.',
              'Select \'+\' icon in the upper-right corner.',
              'Enter the user\'s first name, last name, email address, password, and phone number, if applicable.',
              'Select the appropriate role: Admin, Staff, Instructor, or Read Only.',
              'If the selected role requires program access, select the applicable program or programs.',
              'Select Add to create the user.',
            ],
          },
        ],
      },
      {
        title: 'Update a User',
        description: 'Edit a user\'s details, role, or assigned programs.',
        icon: 'heroicons:pencil-square',
        sections: [
          {
            question: 'How do I update an existing user (Admin, Staff, or Instructor), including their name, email, password, or role?',
            steps: [
              'From the dashboard, select the Users card.',
              'Search for and select the user you want to update.',
              'Open the user\'s profile.',
              'Update the applicable information, such as the user\'s name, email address, password, or role.',
              'To change the user\'s role, select the applicable role tab: Admin, Staff, Instructor, or Read Only.',
              'If Instructor is selected, update the user\'s assigned programs as needed.',
              'Select Update to apply the changes.',
            ],
          },
        ],
      },
      {
        title: 'Assign an Instructor',
        description: 'Give an instructor access to the right programs.',
        icon: 'heroicons:user-group',
        sections: [
          {
            question: 'How do I assign an instructor to a program so they can view the program and check students in and out?',
            heading: 'Option 1: From the Program Details page',
            steps: [
              'From the dashboard, select the Programs card and select the applicable program.',
              'Open the program details for editing.',
              'Select the instructor from the Select Teacher field.',
              'Select Save.',
            ],
          },
          {
            heading: 'Option 2: From the Users page',
            steps: [
              'From the dashboard, select the Users card.',
              'Search for and select the instructor.',
              'Open the instructor\'s profile.',
              'In the Role section, select the Instructor tab if not selected already.',
              'Select the applicable program or programs from the Select Program dropdown.',
              'Select Update.',
            ],
          },
        ],
      },
      {
        title: 'Delete a User',
        description: 'Remove an existing user from the system.',
        icon: 'heroicons:trash',
        sections: [
          {
            question: 'How do I delete an existing user?',
            steps: [
              'From the dashboard, select the Users card.',
              'Search for and select the user you want to delete.',
              'Open the user\'s profile.',
              'Select Delete.',
              'A deletion warning will appear. Select Proceed Anyway to confirm the deletion.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Zone Management',
    articles: [
      {
        title: 'Add, Edit, or Delete a Zone',
        description: 'Create zones and manage their information.',
        icon: 'heroicons:map-pin',
        sections: [
          {
            question: 'How do I add, edit, or delete a zone?',
            heading: 'To add a zone',
            steps: [
              'From the Dashboard, select the Zones card.',
              'On the Zones page, select the + icon in the upper-right corner.',
              'Enter the zone information.',
              'Select Save.',
            ],
          },
          {
            heading: 'To edit or delete a zone',
            steps: [
              'From the Dashboard, select the Zones card.',
              'Select the name of the zone you want to manage.',
              'In the Zone Information window, update the zone information and select Update.',
              'To delete the zone, select Delete.',
              'Select Cancel if you want to close the window without making changes.',
            ],
          },
        ],
      },
      {
        title: 'Assign Users to Zones',
        description: 'Assign, unassign, or move a user to another zone.',
        icon: 'heroicons:user-group',
        sections: [
          {
            question: 'How do I assign a user to a zone?',
            steps: [
              'From the Dashboard, select the Zones card.',
              'Locate the applicable zone and select Assign.',
              'Open the Select Teacher dropdown.',
              'Select the Admin, Staff member, or Instructor you want to assign.',
              'Select Assign to confirm the assignment.',
            ],
          },
          {
            note: 'Users who are already assigned to another zone will appear grayed out and cannot be selected. Each user can be assigned to only one zone at a time.',
            heading: 'To unassign or reassign a user',
            steps: [
              'Select the name of the user currently assigned to the zone.',
              'Select Unassign to remove the user from the zone.',
              'To assign the user to another zone, locate the new zone and select Assign.',
              'Select the user from the Select Teacher dropdown.',
              'Select Assign to confirm the new assignment.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Manage Students or Participants',
    articles: [
      {
        title: 'Add a Student or Participant',
        description: 'Add a profile or register through the Registration Portal.',
        icon: 'heroicons:user-plus',
        sections: [
          {
            question: 'How do I add a new student or participant?',
            heading: 'Option 1: From the Add Student page',
            steps: [
              'From the dashboard, select the Add Students card.',
              'Enter the required student or participant information.',
              'Select the applicable Grade and Program from the dropdown fields, if required.',
              'Select Save.',
            ],
          },
          {
            heading: 'Option 2: Through the Registration Portal',
            steps: [
              'The parent or participant opens the Registration Portal using the provided registration link.',
              'They enter the required participant and registration information.',
              'They select the applicable program.',
              'They review the information and submit the registration.',
            ],
          },
        ],
      },
      {
        title: 'Find a Student or Participant',
        description: 'Search all participants or within a program.',
        icon: 'heroicons:magnifying-glass',
        sections: [
          {
            question: 'How do I search for an existing student or participant?',
            heading: 'Option 1: From the All-Participants page',
            steps: [
              'Select the Search icon from the bottom menu.',
              'Enter the student\'s or participant\'s name in the search bar.',
              'Select the applicable profile from the search results.',
            ],
          },
          {
            heading: 'Option 2: From a specific program',
            steps: [
              'From the dashboard, select the program in which the student or participant is enrolled.',
              'Enter the student\'s or participant\'s name in the search bar.',
              'Select the applicable profile from the search results.',
            ],
          },
        ],
      },
      {
        title: 'Update Student Information',
        description: 'Edit a profile image, name, allergies, or grade.',
        icon: 'heroicons:pencil-square',
        sections: [
          {
            question: 'How do I update a student or participant\'s information, such as their image, name, allergies, or grade?',
            steps: [
              'Select the search icon from the bottom menu.',
              'Search for the student or participant.',
              'Select their profile card.',
              'Update the applicable information, such as the profile image, name, allergies, or grade.',
              'Select Save.',
            ],
          },
        ],
      },
      {
        title: 'Manage Student Programs',
        description: 'Assign, reassign, or unassign program enrollment.',
        icon: 'heroicons:cog-6-tooth',
        sections: [
          {
            question: 'How do I assign, reassign, or unassign a student or participant to or from one or more programs?',
            steps: [
              'Select the Search icon from the bottom menu.',
              'Enter the student\'s or participant\'s name in the search bar.',
              'Select the applicable profile from the search results.',
              'Scroll to the Program section and select the + icon.',
              'On the student information screen, select the applicable grade from the Grade dropdown. Only one grade can be selected.',
              'Open the Select Program dropdown to view all available programs.',
              'Check the applicable program boxes to assign the student. Multiple programs can be selected.',
              'To unassign the student from a program, uncheck the applicable program box.',
              'To reassign the student, uncheck the current program and check the new program.',
              'Select Update to apply the changes.',
            ],
          },
        ],
      },
      {
        title: 'Manage Authorized Pickups',
        description: 'Add, edit, or delete an authorized pickup person.',
        icon: 'heroicons:user-group',
        sections: [
          {
            question: 'How do I add, edit, or delete an authorized pickup, such as a parent, grandparent, sibling, babysitter, or other authorized person?',
            heading: 'To add a guardian or authorized pickup',
            steps: [
              'Select the Search icon from the bottom menu.',
              'Enter the student\'s or participant\'s name in the search bar.',
              'Navigate to the applicable student\'s profile.',
              'Select the + icon beside Authorized Pickups.',
              'Upload a photo and enter the person\'s first name, last name, phone number, and email address.',
              'Select the applicable relationship and preferences.',
              'Select Save.',
            ],
          },
          {
            heading: 'To edit an authorized pickup',
            steps: [
              'Navigate to the student\'s profile.',
              'Select the authorized pickup you want to edit.',
              'Update the applicable information.',
              'Select Save.',
            ],
          },
          {
            heading: 'To delete an authorized pickup',
            steps: [
              'Navigate to the student\'s profile.',
              'Select the authorized pickup you want to delete.',
              'Select Delete.',
              'Select Proceed Anyway to confirm the deletion.',
            ],
          },
          {
            note: 'Parents cannot be deleted. The delete option applies only to other authorized pickups.',
          },
        ],
      },
      {
        title: 'Delete a Student or Participant',
        description: 'Remove a student or participant profile.',
        icon: 'heroicons:trash',
        sections: [
          {
            question: 'How do I delete a student or participant from the system?',
            steps: [
              'Select the Search icon from the bottom menu.',
              'Enter the student\'s or participant\'s name in the search bar.',
              'Select the applicable profile from the search results.',
              'Select the three-dot menu in the upper-right corner of the profile.',
              'Select Delete.',
              'Select Proceed Anyway to confirm the deletion.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Attendance',
    articles: [
      {
        title: 'Start Attendance',
        description: 'Begin today\'s attendance session for a program.',
        icon: 'heroicons:check-circle',
        sections: [
          {
            question: 'How do I start attendance for a program today?',
            steps: [
              'All programs scheduled for the current day will be displayed under Today\'s Schedule on the dashboard.',
              'Select the applicable program.',
              'Select the Start Attendance icon (the hexagon containing a check mark).',
              'A confirmation pop-up will appear. Select Start Attendance to begin.',
            ],
          },
        ],
      },
      {
        title: 'Check In a Student',
        description: 'Change a student\'s status from Absent to Checked In.',
        icon: 'heroicons:check-circle',
        sections: [
          {
            question: 'How do I check in a student?',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'Start attendance for the program. When attendance begins, all students will initially be marked Absent.',
              'Locate the student and tap their Absent status once.',
              'The status will turn green and change to Checked In.',
            ],
          },
        ],
      },
      {
        title: 'View Pickup Requests',
        description: 'See Ready to Pickup statuses and notifications.',
        icon: 'heroicons:bell-alert',
        sections: [
          {
            question: 'How do I check whether a parent has initiated a pickup request for a student?',
            heading: 'From the Attendance page',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'When a parent submits a Ready to Pickup request, the student\'s attendance status will automatically turn purple.',
              'The purple status indicates that the student is ready to be picked up.',
            ],
          },
          {
            heading: 'From the Notifications page',
            steps: [
              'When a parent submits a Ready to Pickup request, a notification banner and sound alert will appear.',
              'Navigate to the Notifications page to view the request.',
              'If necessary, select the Refresh icon to update the notification list.',
            ],
          },
        ],
      },
      {
        title: 'Mark a Student With Teacher',
        description: 'Select With Teacher from the attendance status options.',
        icon: 'heroicons:user-plus',
        sections: [
          {
            question: 'How do I mark a student as "With Teacher"?',
            steps: [
              'Navigate to the program in which the student is enrolled and start attendance.',
              'Press and hold the student\'s current attendance status.',
              'Select With Teacher from the available status options.',
            ],
          },
        ],
      },
      {
        title: 'Mark a Student On the Way',
        description: 'Find the options for updating a student\'s pickup status.',
        icon: 'heroicons:paper-airplane',
        sections: [
          {
            question: 'If a student is already checked in and/or a parent has initiated a pickup request, how do I mark the student as "On the Way" so the parent or other school staff are notified?',
            heading: 'Using long press',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'Press and hold the student\'s current attendance status (Ready to Pickup).',
              'Select On the Way.',
            ],
          },
          {
            heading: 'From the Notifications page',
            steps: [
              'Select the bell icon from the bottom menu & navigate to the Notifications page.',
              'Locate the student\'s Ready to Pickup request.',
              'Select Ready to Pickup on the student\'s card to open the Signature page.',
              'On the Signature page, select On the Way.',
            ],
          },
          {
            heading: 'From the Checkout screen',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'Select the student card to open the Signature page.',
              'On the Signature page, add signature and select Checked Out.',
            ],
          },
        ],
      },
      {
        title: 'Check Out a Student',
        description: 'Check out from attendance or the Notifications page.',
        icon: 'heroicons:arrow-right-on-rectangle',
        sections: [
          {
            question: 'How do I check out a student?',
            heading: 'From the Attendance page',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'Press and hold the student\'s current attendance status (Ready to Pickup/On the Way).',
              'Select Checked Out.',
            ],
          },
          {
            heading: 'From the Notifications page',
            steps: [
              'Select the bell icon from the bottom menu and navigate to the Notifications page.',
              'Locate the student\'s Ready to Pickup/On the Way request.',
              'Select the student\'s card to open the Signature page.',
              'On the Signature page, select Checked Out.',
            ],
          },
        ],
      },
      {
        title: 'Undo an Attendance Action',
        description: 'Change a student back to the correct previous status.',
        icon: 'heroicons:arrow-uturn-left',
        sections: [
          {
            question: 'How do I undo or revert an attendance action?',
            steps: [
              'Navigate to the applicable program and start attendance.',
              'Press and hold the student\'s current attendance status.',
              'Select the correct previous status from the available options.',
            ],
          },
        ],
      },
      {
        title: 'Take Attendance Offline',
        description: 'Record attendance when there is no internet connection.',
        icon: 'heroicons:signal-slash',
        sections: [
          {
            question: 'How do I take attendance offline?',
            steps: [
              'Navigate to the applicable program and start attendance as usual.',
              'When the internet connection is unavailable, a No Internet Connection banner will appear.',
              'Continue recording attendance using the normal process.',
              'Attendance changes made while offline will be saved on the device.',
              'The system will display the number of attendance entries that have not yet been synced.',
            ],
          },
        ],
      },
      {
        title: 'Sync Offline Attendance',
        description: 'Sync saved entries when the internet connection returns.',
        icon: 'heroicons:arrow-path',
        sections: [
          {
            question: 'How do I sync the offline attendance once my internet connection is restored?',
            steps: [
              'Confirm that the device is connected to the internet.',
              'Select the red banner displaying the number of unsynced entries and the message Click here to sync.',
              'The Sync Status page will open and display the applicable student and attendance details.',
              'Select the Sync All icon.',
              'The saved attendance records will sync with the system and become available to other authorized users assigned to the program.',
            ],
          },
        ],
      },
      {
        title: 'End Attendance',
        description: 'Close a session, with or without a notification.',
        icon: 'heroicons:check-circle',
        sections: [
          {
            question: 'How do I end attendance for a program for the day?',
            steps: [
              'Navigate to the program with an active attendance session.',
              'Select the Attendance icon (the hexagon containing a green check mark).',
              'A confirmation pop-up will appear with two options:',
              'Select End Attendance to end the attendance session.',
              'Select End and Send Notification to end the attendance session and send a notification.',
            ],
          },
        ],
      },
      {
        title: 'View On the Way Notifications',
        description: 'Find and refresh On the Way notifications.',
        icon: 'heroicons:bell-alert',
        sections: [
          {
            question: 'How do I check on the way notifications?',
            steps: [
              'Navigate to the Notifications page.',
              'If necessary, select the Refresh icon in the upper-right corner.',
              'All available On the Way notifications will be displayed on the page.',
            ],
          },
        ],
      },
      {
        title: 'Check Out Without a Signature',
        description: 'Check out a student using their attendance status.',
        icon: 'heroicons:arrow-right-on-rectangle',
        sections: [
          {
            question: 'How do I check out students without parent signature?',
            steps: [
              'Navigate to the program in which the student is enrolled.',
              'Press and hold the student\'s Checked In status.',
              'Select Checked Out.',
            ],
          },
        ],
      },
      {
        title: 'Check Out Multiple Students',
        description: 'Check out siblings or students under the same parent.',
        icon: 'heroicons:user-group',
        sections: [
          {
            question: 'How do I check out multiple students at a time under the same parent?',
            steps: [
              'Navigate to the Notifications page.',
              'Select Ready to Pickup on one of the student cards.',
              'On the Signature page, select Select All to choose all students under the same parent. Alternatively, select the individual checkboxes for the students you want to check out.',
              'Select Check Out.',
            ],
          },
        ],
      },
      {
        title: 'Revert an Excused Status',
        description: 'Change a student\'s status from Excused to Checked In.',
        icon: 'heroicons:arrow-uturn-left',
        sections: [
          {
            question: 'How do I revert from the excused status?',
            steps: [
              'Navigate to the applicable program and start attendance.',
              'Press and hold the student\'s Excused status.',
              'Select Checked In.',
            ],
          },
        ],
      },
      {
        title: 'Sync Attendance',
        description: 'Send unsynced attendance records to the system.',
        icon: 'heroicons:arrow-path',
        sections: [
          {
            question: 'How do I sync my attendance?',
            steps: [
              'Confirm that the device is connected to the internet.',
              'Select the red banner displaying the number of unsynced attendance entries and the message Click here to sync.',
              'The Sync Status page will open and display the applicable student and attendance details.',
              'Select the Sync All icon.',
              'The attendance records will sync with the system and become available to other authorized users assigned to the program.',
            ],
          },
        ],
      },
      {
        title: 'Set Up Student Self-Checkout',
        description: 'Add a student as an authorized self-checkout pickup.',
        icon: 'heroicons:user-plus',
        sections: [
          {
            question: 'How do I add students for self-checkout?',
            steps: [
              'Navigate to the student\'s profile.',
              'Select the + icon beside the Authorized Pickups title.',
              'Enter the name and required details of the student who will be authorized for self-checkout.',
              'Select Save.',
            ],
          },
        ],
      },
    ],
  },
]

const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) return categories

  const query = searchQuery.value.toLowerCase().trim()
  return categories
    .map((category) => ({
      ...category,
      articles: category.articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.description.toLowerCase().includes(query) ||
          category.name.toLowerCase().includes(query)
      ),
    }))
    .filter((category) => category.articles.length > 0)
})

watch(selectedArticle, () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>

<style scoped>
</style>
